import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EventUpdateSchema, type ReminderChannel } from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { fieldErrors, normalizePhoneE164NANP } from '../lib/form-helpers.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Checkbox, Input, Select } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';

const REMINDER_OFFSETS: { value: string; label: string }[] = [
  { value: 'PT24H', label: '24 hours before' },
  { value: 'PT48H', label: '48 hours before' },
  { value: 'PT72H', label: '72 hours before' },
  { value: 'P1W', label: '1 week before' },
  { value: 'P1M', label: '1 month before' },
  { value: 'PT1H', label: '1 hour before' },
];

const ALL_CHANNELS: ReminderChannel[] = ['voice', 'sms', 'email'];

interface FormState {
  title: string;
  attendee_name: string;
  attendee_email: string;
  attendee_phone: string;
  start_at: string;
  end_at: string;
  message_template_id: string;
  reminderChannels: ReminderChannel[];
  reminderOffset: string;
}

const EMPTY: FormState = {
  title: '',
  attendee_name: '',
  attendee_email: '',
  attendee_phone: '',
  start_at: '',
  end_at: '',
  message_template_id: '',
  reminderChannels: [],
  reminderOffset: 'PT24H',
};

interface EventReminder {
  channels: ReminderChannel[];
  offset: string;
}

interface EventDetail {
  id: string;
  title: string;
  attendee_name?: string;
  attendee_email?: string;
  attendee_phone: string;
  start_at: string;
  end_at: string;
  message_template_id?: string;
  reminders: EventReminder[];
  status: string;
}

interface ListResp<T> { data: T[]; pagination: unknown }
interface MessageTemplateLite { id: string; name: string }

export function EditEventPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);

  const eventQ = useQuery({
    queryKey: ['event', id],
    queryFn: () => api.get(`/v1/events/${id}`) as Promise<EventDetail>,
    enabled: !!id,
  });

  const templatesQ = useQuery({
    queryKey: ['message-templates'],
    queryFn: () => api.get('/v1/message-templates') as Promise<ListResp<MessageTemplateLite>>,
  });

  useEffect(() => {
    if (!eventQ.data) return;
    const e = eventQ.data;
    const firstReminder = e.reminders[0];
    setForm({
      title: e.title ?? '',
      attendee_name: e.attendee_name ?? '',
      attendee_email: e.attendee_email ?? '',
      attendee_phone: e.attendee_phone ?? '',
      start_at: isoToLocalInput(e.start_at),
      end_at: isoToLocalInput(e.end_at),
      message_template_id: e.message_template_id ?? '',
      reminderChannels: firstReminder ? firstReminder.channels : [],
      reminderOffset: firstReminder ? firstReminder.offset : 'PT24H',
    });
  }, [eventQ.data]);

  const save = useMutation({
    mutationFn: (payload: unknown) => api.patch(`/v1/events/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['event', id] });
      navigate('/events');
    },
    onError: (err) => setBannerError(extractDetail(err)),
  });

  const update = <K extends keyof FormState>(key: K) => (value: FormState[K]): void => {
    setForm((f) => ({ ...f, [key]: value }));
  };
  const toggleChannel = (c: ReminderChannel): void => {
    setForm((f) => ({
      ...f,
      reminderChannels: f.reminderChannels.includes(c)
        ? f.reminderChannels.filter((x) => x !== c)
        : [...f.reminderChannels, c],
    }));
  };

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (save.isPending) return; // double-save guard
    setBannerError(null);

    const payload: Record<string, unknown> = {
      title: form.title.trim(),
      attendee_phone: normalizePhoneE164NANP(form.attendee_phone) ?? form.attendee_phone.trim(),
      // ^ if normalize returns null, the schema below will reject it.
      start_at: form.start_at ? new Date(form.start_at).toISOString() : '',
      end_at: form.end_at ? new Date(form.end_at).toISOString() : '',
      reminders:
        form.reminderChannels.length > 0
          ? [{ channels: form.reminderChannels, offset: form.reminderOffset }]
          : [],
    };
    if (form.attendee_name.trim()) payload['attendee_name'] = form.attendee_name.trim();
    if (form.attendee_email.trim()) payload['attendee_email'] = form.attendee_email.trim();
    if (form.message_template_id) payload['message_template_id'] = form.message_template_id;

    const fieldIssues = fieldErrors(EventUpdateSchema, payload);
    if (fieldIssues) {
      setErrors(fieldIssues);
      return;
    }
    setErrors(null);
    save.mutate(payload);
  };

  const templates = useMemo(() => templatesQ.data?.data ?? [], [templatesQ.data]);

  if (eventQ.isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (eventQ.error) return <FormBanner message={extractDetail(eventQ.error)} />;

  return (
    <div className="max-w-2xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Edit event</h1>
        <p className="text-sm text-ink-muted">
          Reminders fire at the chosen offset before <code>start_at</code>. Past or already-sent
          reminders are not retroactively re-fired.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Event</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {bannerError && <FormBanner message={bannerError} />}

          <form onSubmit={onSubmit} className="space-y-4">
            <FormField label="Title" required error={errors?.['title']}>
              {(id) => (
                <Input id={id} value={form.title} onChange={(e) => update('title')(e.target.value)} required autoFocus />
              )}
            </FormField>

            <fieldset className="rounded border border-border p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-ink-muted">Attendee</legend>
              <FormField label="Name" error={errors?.['attendee_name']}>
                {(id) => <Input id={id} value={form.attendee_name} onChange={(e) => update('attendee_name')(e.target.value)} />}
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Phone" required hint="E.164" error={errors?.['attendee_phone']}>
                  {(id) => (
                    <Input
                      id={id}
                      value={form.attendee_phone}
                      onChange={(e) => update('attendee_phone')(e.target.value)}
                      required
                      placeholder="+15555550100"
                    />
                  )}
                </FormField>
                <FormField label="Email" error={errors?.['attendee_email']}>
                  {(id) => (
                    <Input
                      id={id}
                      type="email"
                      value={form.attendee_email}
                      onChange={(e) => update('attendee_email')(e.target.value)}
                    />
                  )}
                </FormField>
              </div>
            </fieldset>

            <fieldset className="rounded border border-border p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-ink-muted">When</legend>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Start" required error={errors?.['start_at']}>
                  {(id) => (
                    <Input
                      id={id}
                      type="datetime-local"
                      value={form.start_at}
                      onChange={(e) => update('start_at')(e.target.value)}
                      required
                    />
                  )}
                </FormField>
                <FormField label="End" required error={errors?.['end_at']}>
                  {(id) => (
                    <Input
                      id={id}
                      type="datetime-local"
                      value={form.end_at}
                      onChange={(e) => update('end_at')(e.target.value)}
                      required
                    />
                  )}
                </FormField>
              </div>
            </fieldset>

            <fieldset className="rounded border border-border p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-ink-muted">Reminder</legend>
              <div className="flex flex-wrap gap-3">
                {ALL_CHANNELS.map((c) => (
                  <label key={c} className="flex items-center gap-1.5 text-sm">
                    <Checkbox
                      checked={form.reminderChannels.includes(c)}
                      onChange={() => toggleChannel(c)}
                    />
                    {c}
                  </label>
                ))}
              </div>
              <FormField label="When to send" error={errors?.['reminders.0.offset']}>
                {(id) => (
                  <Select
                    id={id}
                    value={form.reminderOffset}
                    onChange={(e) => update('reminderOffset')(e.target.value)}
                  >
                    {REMINDER_OFFSETS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
            </fieldset>

            <FormField label="Message template" error={errors?.['message_template_id']}>
              {(id) => (
                <Select
                  id={id}
                  value={form.message_template_id}
                  onChange={(e) => update('message_template_id')(e.target.value)}
                >
                  <option value="">— None —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Select>
              )}
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate('/events')}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    return String((err.body as { detail: unknown }).detail);
  }
  return (err as Error).message;
}
